import {EMPTY_LIST, wrapInterfaceId} from "@cm/template-nodes"
import type {
    BuilderOutlet,
    GraphBuilderScope,
    NodeEvaluator,
    IGetInfoNodeParts,
    OnCompileContext,
    OnFilterActiveContext,
    InterfaceDescriptor,
    TemplateNode,
} from "@cm/template-nodes"
import type {MattressTexUVLayout} from "#mattress/geometry/mattress"
import type {
    MattressFocusedComponentInstancePath,
    MattressNodePath,
    MattressOnCompileContext,
    MattressOnFilterActiveContext,
} from "#mattress/nodes/component/mattress-component-types"
import {isMattressFocusedComponentInstancePath} from "#mattress/nodes/component/mattress-component-types"
import type {MattressReferenceFrameLikeNode, MattressCurveLikeNode} from "#mattress/role-markers"
import type {MattressNode} from "#mattress/nodes/mattress-node"
import type {MattressReferenceFrameData} from "#mattress/nodes/reference-frame/mattress-reference-frame"
import type {MattressFabricLayout} from "#mattress/nodes/mattress-cut"
import type {MattressComponentInterfaceDescriptor} from "#mattress/nodes/component/mattress-component-interface-descriptors"
import type {MattressCurveData} from "#mattress/nodes/curve/mattress-curve"

import type {Vector2Like} from "@cm/math"

import {IsNotUndefined} from "@cm/utils"

/////////////////////////////////////////////////////////////////
// Reference-frame helpers.

export function mattressReferenceFramesDataListsPush(
    scope: GraphBuilderScope,
    referenceFramesDataLists: BuilderOutlet<MattressReferenceFrameData[]>[],
    referenceFrameData: BuilderOutlet<MattressReferenceFrameData | null>,
) {
    referenceFramesDataLists.push(
        scope.pureLambda(
            referenceFrameData,
            (referenceFrameData) => (referenceFrameData ? [referenceFrameData] : EMPTY_LIST),
            "mattressReferenceFrameDataLists",
        ),
    )
}

export function aliasReferenceMattressFrameData(
    instance: MattressReferenceFrameLikeNode,
    evaluator: NodeEvaluator,
    referenceFrameData: BuilderOutlet<MattressReferenceFrameData | null>,
) {
    const localId = evaluator.getLocalId(instance)
    evaluator.templateScope.alias(referenceFrameData, `mattressReferenceFrameData-${localId}`)
}

export function resolveMattressReferenceFrameData(
    instance: MattressReferenceFrameLikeNode,
    scope: GraphBuilderScope,
    evaluator: NodeEvaluator,
): BuilderOutlet<MattressReferenceFrameData | null> {
    return scope.unresolvedToNull(evaluator.templateScope.resolve<MattressReferenceFrameData>(`mattressReferenceFrameData-${evaluator.getLocalId(instance)}`))
}

/////////////////////////////////////////////////////////////////
// To/from-global UV-space helpers.

type MattressReferenceFrameNode = MattressNode & MattressReferenceFrameLikeNode

function getReferenceFrameData(node: MattressReferenceFrameNode, context: IGetInfoNodeParts): MattressReferenceFrameData | null {
    return node.getParentMattressNode().getReferenceFrameData(node, context)
}

export function mattressReferenceFrameToGlobalUVSpace(node: MattressReferenceFrameNode, uv: Vector2Like, context: IGetInfoNodeParts): Vector2Like {
    const data = getReferenceFrameData(node, context)
    if (!data) throw new Error("Reference frame data not available")
    return data.toGlobalSpace(uv)
}

export function mattressReferenceFrameFromGlobalUVSpace(node: MattressReferenceFrameNode, uv: Vector2Like, context: IGetInfoNodeParts): Vector2Like {
    const data = getReferenceFrameData(node, context)
    if (!data) throw new Error("Reference frame data not available")
    return data.fromGlobalSpace(uv)
}

/////////////////////////////////////////////////////////////////
// Mattress on-context other-data helpers.

export type MattressOnContextOtherData = {
    focusedComponentInstancePath?: MattressFocusedComponentInstancePath
}

// Writes the per-mattress other-data entry (keyed by mattress.instanceId). The provider abstracts how
// the entry actually lands in the compilation context — caller responsibility.
export function setMattressOnContextOtherData(
    mattress: TemplateNode,
    data: MattressOnContextOtherData | undefined,
    provider: {setCompilationContextOtherDataEntry: (key: string, data: unknown) => void},
) {
    provider.setCompilationContextOtherDataEntry(mattress.instanceId, data)
}

// `mattress.instanceId` is the key under context.otherData; the value carries focus state.
export function parseMattressOnContextOtherData(
    mattress: TemplateNode,
    context: {otherData?: Record<string, unknown>},
): MattressOnContextOtherData & {focusedComponentInstancePath: MattressFocusedComponentInstancePath} {
    const mattressOther = context.otherData?.[mattress.instanceId]

    const result: MattressOnContextOtherData & {focusedComponentInstancePath: MattressFocusedComponentInstancePath} = {
        focusedComponentInstancePath: "root",
    }
    if (typeof mattressOther !== "object" || mattressOther === null || Array.isArray(mattressOther)) return result
    if ("focusedComponentInstancePath" in mattressOther && isMattressFocusedComponentInstancePath(mattressOther.focusedComponentInstancePath))
        result.focusedComponentInstancePath = mattressOther.focusedComponentInstancePath
    return result
}

/////////////////////////////////////////////////////////////////
// Node-path helpers.

export function isNodePathPrefixOf(a: MattressNodePath, b: MattressNodePath): boolean {
    return a.length <= b.length && a.every((node, idx) => node === b[idx])
}

/////////////////////////////////////////////////////////////////
// Mattress mesh-segmentation helpers.

export function mattressFabricLayoutToTexUVLayout(fabricLayout: MattressFabricLayout): MattressTexUVLayout {
    return `texUV_${fabricLayout}`
}

/////////////////////////////////////////////////////////////////
// Mattress context-narrowing helpers — the abstract base accepts `OnCompileContext` / `OnFilterActiveContext`,
// the mattress runtime extends them with `mattressContext`. These helpers concentrate the type narrowing.

export function getMattressCompileContext(context: OnCompileContext): MattressOnCompileContext["mattressContext"] {
    const mattressContext = (context as MattressOnCompileContext).mattressContext
    if (!mattressContext) throw new Error("MattressOnCompileContext.mattressContext is missing")
    return mattressContext
}

// Returns `undefined` when the mattress runtime hasn't set up a component-compilation visit. Callers
// branch on the presence of the result (e.g. external vs. internal visit in MattressComponentGraph).
export function getMattressFilterActiveContext(context: OnFilterActiveContext): MattressOnFilterActiveContext["mattressContext"] {
    return (context as MattressOnFilterActiveContext).mattressContext
}

/////////////////////////////////////////////////////////////////
// Descriptor + export helpers.

export function filterAndWrapInterface<T extends InterfaceDescriptor<unknown> | MattressComponentInterfaceDescriptor<unknown>>(
    descriptorList: T[],
    componentInstanceId: string | undefined,
    claimedInputIds: string[],
    exposeClaimedSubComponentInputs: boolean,
    affectedOutputIdWrapId: string | undefined = componentInstanceId,
): T[] {
    return descriptorList
        .filter((d) => IsNotUndefined(d.props))
        .filter((d) => {
            if (d.props.type === "output") return true
            else return claimedInputIds.includes(d.props.id) ? exposeClaimedSubComponentInputs : true
        })
        .map((d) => {
            const cloned = d.clone(componentInstanceId !== undefined ? wrapInterfaceId(componentInstanceId, d.props.id) : d.props.id) as T
            if (affectedOutputIdWrapId !== undefined && "affectedOutputIds" in cloned.props && cloned.props.affectedOutputIds) {
                cloned.props.affectedOutputIds = cloned.props.affectedOutputIds.map((outputId: string) => wrapInterfaceId(affectedOutputIdWrapId, outputId))
            }
            return cloned
        })
}

/////////////////////////////////////////////////////////////////
// Mattress curve data alias/resolve (mirrors the reference-frame variants).

export function aliasMattressCurveData(instance: MattressCurveLikeNode, evaluator: NodeEvaluator, curveData: BuilderOutlet<MattressCurveData | null>): void {
    const localId = evaluator.getLocalId(instance)
    evaluator.templateScope.alias(curveData, `mattressCurveData-${localId}`)
}

export function resolveMattressCurveData(
    instance: MattressCurveLikeNode,
    scope: GraphBuilderScope,
    evaluator: NodeEvaluator,
): BuilderOutlet<MattressCurveData | null> {
    return scope.unresolvedToNull(evaluator.templateScope.resolve<MattressCurveData>(`mattressCurveData-${evaluator.getLocalId(instance)}`))
}

/////////////////////////////////////////////////////////////////
// Interface-id prefix helpers.

export function isInterfaceIdPrefix(interfaceId: string, prefix: string): boolean {
    return interfaceId.startsWith(`${prefix}/`)
}

export function unwrapPrefixFromInterfaceId(prefix: string, interfaceId: string): string {
    if (!isInterfaceIdPrefix(interfaceId, prefix)) throw new Error(`Interface ID "${interfaceId}" does not start with prefix "${prefix}"`)
    return interfaceId.slice(prefix.length + 1)
}

/////////////////////////////////////////////////////////////////
// Local-sub-template-instance id helpers.

export function isLocalTemplateSubInstanceId(id: string): boolean {
    return id.startsWith("local-sub-template-instance-")
}
